// Web Push + SMS helpers (frontend) — subscribe to VAPID and register the phone number.
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

export async function pushSupported() {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export async function pushStatus() {
  if (!(await pushSupported())) return { supported: false, subscribed: false, permission: "unsupported" };
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  return {
    supported: true,
    subscribed: !!sub,
    permission: Notification.permission,
    endpoint: sub?.endpoint || null,
  };
}

export async function pushSubscribe() {
  if (!(await pushSupported())) throw new Error("Push not supported on this device");
  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error("Push permission denied");

  const reg = await navigator.serviceWorker.ready;
  const existing = await reg.pushManager.getSubscription();
  if (existing) {
    // re-register on server in case DB was wiped
    await axios.post(`${API}/push/subscribe`, { subscription: existing.toJSON(), userAgent: navigator.userAgent });
    return existing;
  }

  const { data } = await axios.get(`${API}/push/vapid-public-key`);
  const pubKey = data.publicKey || process.env.REACT_APP_VAPID_PUBLIC_KEY;
  if (!pubKey) throw new Error("VAPID key not configured");

  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(pubKey),
  });
  await axios.post(`${API}/push/subscribe`, { subscription: sub.toJSON(), userAgent: navigator.userAgent });
  return sub;
}

export async function pushUnsubscribe() {
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  if (sub) {
    await sub.unsubscribe();
    await axios.post(`${API}/push/unsubscribe`, { endpoint: sub.endpoint }).catch(() => {});
  }
}

export async function pushTest() {
  const r = await axios.post(`${API}/push/test`);
  return r.data;
}

// ── Digest mode ──
export async function pushGetStatus(endpoint) {
  if (!endpoint) return null;
  try {
    const r = await axios.get(`${API}/push/status`, { params: { endpoint } });
    return r.data;
  } catch (_) {
    return null;
  }
}

export async function pushSetDigestMode(endpoint, enabled) {
  const r = await axios.post(`${API}/push/digest-mode`, { endpoint, enabled });
  return r.data;
}

export async function pushTestAlert() {
  const r = await axios.post(`${API}/push/test-alert`);
  return r.data;
}

// ── SMS ──
export async function smsSubscribe(phone) {
  const r = await axios.post(`${API}/sms/subscribe`, { phone });
  return r.data;
}

export async function smsUnsubscribe(phone) {
  const r = await axios.post(`${API}/sms/unsubscribe`, { phone });
  return r.data;
}

export async function smsTest(phone) {
  const r = await axios.post(`${API}/sms/test`, phone ? { phone } : {});
  return r.data;
}

export async function smsList() {
  const r = await axios.get(`${API}/sms/subscribers`);
  return r.data;
}
